import AddIcon from '@mui/icons-material/Add';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import BubbleChartOutlinedIcon from '@mui/icons-material/BubbleChartOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { isEmpty } from 'es-toolkit/compat';
import React from 'react';

import { MarqueeHighlightText } from 'components/atoms/MarqueeHighlightText';

const chipBadgePop = keyframes`
  0%, 78%, 100% {
    opacity: 0;
    transform: scale(0.65) translateY(2px);
  }

  84% {
    opacity: 0.55;
    transform: scale(1.08) translateY(-1px);
  }

  90% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }

  96% {
    opacity: 0;
    transform: scale(0.9) translateY(-2px);
  }
`;

interface Props {
  selectedStudyId?: string;
  selectedStudyIds?: string[];
  hasAvailableStudies?: boolean;
  onCreateStudy?: () => void;
  onSuggestPrompt?: (prompt: string) => void;
}

interface AgentSuggestion {
  icon: React.ReactElement;
  label: string;
  prompt?: string;
  clickable: boolean;
}

const agentSuggestions = [
  {
    icon: <SummarizeOutlinedIcon fontSize="small" />,
    label: 'Describe the cohort',
    clickable: true,
  },
  {
    icon: <BiotechOutlinedIcon fontSize="small" />,
    label: 'Show biomarker expression',
    prompt:
      'Analyze biomarker expression across the selected studies. Summarize the dominant expression patterns, highlight notable markers, and add charts or tables where they clarify the findings.',
    clickable: true,
  },
  {
    icon: <BubbleChartOutlinedIcon fontSize="small" />,
    label: 'Cell-level distributions',
    clickable: false,
  },
  {
    icon: <ScienceOutlinedIcon fontSize="small" />,
    label: 'Spatial feature analysis',
    prompt:
      'Run a spatial feature analysis for the selected studies. Focus on spatial organization, neighborhood or proximity patterns, and add charts or report sections for the strongest findings.',
    clickable: true,
  },
  {
    icon: <TimelineOutlinedIcon fontSize="small" />,
    label: 'Clinical correlations',
    prompt:
      'Look for clinical correlations in the selected studies. Compare relevant features against available clinical variables, call out the strongest associations, and note sample-size or coverage limitations.',
    clickable: true,
  },
  {
    icon: <AssessmentOutlinedIcon fontSize="small" />,
    label: 'Executive summary',
    prompt:
      'Draft an executive summary for this study report. If uploaded clinical datasets exist, check their cohort join coverage first and use matched rows only for clinical conclusions.',
    clickable: true,
  },
] as AgentSuggestion[];

const StudyReportWorkspaceEmptyState: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  selectedStudyId,
  selectedStudyIds,
  hasAvailableStudies = true,
  onCreateStudy,
  onSuggestPrompt,
}) => {
  const hasStudies = !isEmpty(selectedStudyIds) || Boolean(selectedStudyId);
  const shouldShowCreateStudy = !hasStudies && !hasAvailableStudies && Boolean(onCreateStudy);

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        minHeight: 400,
        p: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={4} sx={{ width: '100%', maxWidth: 640, alignItems: 'center', textAlign: 'center' }}>
        <Stack spacing={1.5}>
          <Typography variant="h4" fontWeight={600}>
            {hasStudies
              ? 'Start building your analysis'
              : hasAvailableStudies
              ? 'Select one or more studies to begin'
              : 'Create a study to begin'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {hasStudies
              ? 'Ask the analysis agent to explore your data — describe the cohort, generate charts, run spatial or clinical comparisons, and assemble the findings into a structured report.'
              : hasAvailableStudies
              ? 'Choose studies from the selector above, then talk to the agent to start your analysis.'
              : 'There are no studies in this lab yet. Create a study to start a new analysis report.'}
          </Typography>
        </Stack>

        {shouldShowCreateStudy && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateStudy}>
            Create study
          </Button>
        )}

        {hasStudies && (
          <Stack spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
            <MarqueeHighlightText variant="overline" color="primary.main" letterSpacing={1.2}>
              Try asking the agent
            </MarqueeHighlightText>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.25, rowGap: 1.5, justifyContent: 'center' }}>
              {agentSuggestions.map((s, index) => (
                <Box key={s.label} sx={{ position: 'relative', display: 'inline-flex' }}>
                  <Box
                    sx={(theme) => ({
                      backgroundColor: theme.palette.text.secondary,
                      boxShadow: `0 4px 10px ${alpha(theme.palette.text.primary, 0.16)}`,
                      position: 'absolute',
                      top: -7,
                      right: -5,
                      zIndex: 1,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.contrastText',
                      opacity: 0,
                      transform: 'scale(0.65)',
                      animation: `${chipBadgePop} 12s ease-in-out infinite`,
                      animationDelay: `${index * 220}ms`,
                      pointerEvents: 'none',
                    })}
                  >
                    <AutoAwesomeRoundedIcon sx={{ fontSize: 11 }} />
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!s.clickable || !onSuggestPrompt}
                    onClick={s.clickable && onSuggestPrompt ? () => onSuggestPrompt(s.prompt || s.label) : undefined}
                    startIcon={s.icon}
                    sx={(theme) => ({
                      backgroundColor:
                        theme.palette.mode === 'light'
                          ? theme.palette.grey[100]
                          : alpha(theme.palette.common.white, 0.08),
                      px: 1.75,
                      py: 0.75,
                      minHeight: 38,
                      height: 38,
                      borderRadius: 999,
                      boxShadow: 'none',
                      color: theme.palette.text.primary,
                      textTransform: 'none',
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      '&:hover':
                        s.clickable && onSuggestPrompt
                          ? {
                              boxShadow: 'none',
                              backgroundColor:
                                theme.palette.mode === 'light'
                                  ? theme.palette.grey[200]
                                  : alpha(theme.palette.common.white, 0.12),
                            }
                          : undefined,
                      '& .MuiSvgIcon-root': {
                        fontSize: 18,
                        color: theme.palette.text.secondary,
                      },
                      '&.Mui-disabled': {
                        color: theme.palette.action.disabled,
                        backgroundColor: theme.palette.action.disabledBackground,
                      },
                    })}
                  >
                    {s.label}
                  </Button>
                </Box>
              ))}
            </Box>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default StudyReportWorkspaceEmptyState;
